import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Send, CheckCircle, Eye, MousePointerClick, BarChart3,
  RefreshCw, FileText, UserX, Globe, ArrowRight, Mail, Settings, Shield
} from 'lucide-react';
import { dashboardApi, preferencesApi, onboardingApi, senderProfileApi } from '../services/emailApi';
import './EmailDashboard.css';

const EmailDashboard = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');
  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unsubStats, setUnsubStats] = useState([]);
  const [unsubLoading, setUnsubLoading] = useState(true);
  const [onboardingSteps, setOnboardingSteps] = useState(null);
  const [onboardingProgress, setOnboardingProgress] = useState(null);
  const [fromPreview, setFromPreview] = useState(null);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await dashboardApi.overview();
      if (res.data?.success) {
        const d = res.data.data;
        setOverview({
          campaigns: d.summary?.campaigns || {},
          emailStats: {
            totalSent: d.summary?.emails?.sent || 0,
            deliveryRate: parseFloat(d.summary?.rates?.deliveryRate || 0),
            openRate: parseFloat(d.summary?.rates?.openRate || 0),
            clickRate: parseFloat(d.summary?.rates?.clickRate || 0),
          },
          recentCampaigns: d.recentCampaigns || [],
          dailyStats: d.dailyStats || [],
        });
      }
    } catch (err) {
      console.error('Dashboard overview error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchChart = useCallback(async () => {
    try {
      setLoadingChart(true);
      const groupBy = period === '24h' ? 'hour' : period === '90d' ? 'week' : 'day';
      const res = await dashboardApi.sendingStats({ period, groupBy });
      if (res.data?.success) {
        const responseData = res.data.data;
        // The API may return dailyStats as a time-series array, or stats as aggregate.
        // Prefer dailyStats (array of per-period objects) for the chart.
        let timeSeriesData = [];
        if (Array.isArray(responseData?.dailyStats) && responseData.dailyStats.length > 0) {
          timeSeriesData = responseData.dailyStats;
        } else if (Array.isArray(responseData?.stats)) {
          timeSeriesData = responseData.stats;
        } else if (responseData?.stats && typeof responseData.stats === 'object') {
          // stats is an aggregate object — wrap as single data point for display
          timeSeriesData = [{
            total: parseInt(responseData.stats.sent || responseData.stats.total || 0, 10),
            delivered: parseInt(responseData.stats.delivered || 0, 10),
            opened: parseInt(responseData.stats.opened || 0, 10),
            period: 'Total'
          }];
        }
        setChartData(timeSeriesData);
      }
    } catch (err) {
      console.error('Chart data error:', err);
    } finally {
      setLoadingChart(false);
    }
  }, [period]);

  const fetchUnsubStats = useCallback(async () => {
    try {
      setUnsubLoading(true);
      const res = await preferencesApi.stats();
      const d = res?.data?.data ?? res?.data ?? [];
      if (Array.isArray(d)) setUnsubStats(d);
    } catch {
      /* silent — widget is non-critical */
    } finally {
      setUnsubLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchChart(); }, [fetchChart]);
  useEffect(() => { fetchUnsubStats(); }, [fetchUnsubStats]);

  /* Fetch onboarding status */
  useEffect(() => {
    (async () => {
      try {
        const res = await onboardingApi.autoDetect();
        if (res.data?.success) {
          setOnboardingSteps(res.data.steps || []);
          setOnboardingProgress(res.data.progress || null);
        }
      } catch {
        /* non-critical — onboarding banner just won't show */
      }
    })();
  }, []);

  /* Fetch sender profile (from-preview) */
  useEffect(() => {
    (async () => {
      try {
        const res = await senderProfileApi.get();
        if (res.data?.success) {
          const d = res.data.data;
          setFromPreview({
            fromAddress: d.fromPreview || '',
            customDomain: d.customDomain || null,
            customDomainVerified: !!d.customDomainVerified,
            customDomainRegistered: !!d.customDomain,
          });
        }
      } catch {
        /* non-critical */
      }
    })();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    Promise.all([fetchOverview(), fetchChart(), fetchUnsubStats()])
      .finally(() => setRefreshing(false));
  };

  /* Onboarding step config */
  const STEP_CONFIG = {
    configure_provider: { icon: <CheckCircle size={16} />, label: 'Provider Ready', desc: 'Email sending is pre-configured', path: '' },
    verify_identity:    { icon: <Globe size={16} />, label: 'Verify Identity', desc: 'Verify a domain or email address', path: '/dashboard/email/settings/custom-domain' },
    create_template:    { icon: <FileText size={16} />, label: 'Create Template', desc: 'Design your first email template', path: '/dashboard/email/templates/new' },
    send_test:          { icon: <Send size={16} />, label: 'Send Campaign', desc: 'Send your first email campaign', path: '/dashboard/email/campaigns/new' },
  };
  const showOnboarding = onboardingProgress && !onboardingProgress.isComplete;

  /* Helpers */
  const fmtNum = (n) => (n ?? 0).toLocaleString();
  const fmtPct = (n) => `${(n ?? 0).toFixed(1)}%`;

  const kpiCards = overview ? [
    { label: 'Total Sent',     value: fmtNum(overview.emailStats?.totalSent),     icon: <Send size={20} />,               color: 'primary' },
    { label: 'Delivery Rate',  value: fmtPct(overview.emailStats?.deliveryRate),   icon: <CheckCircle size={20} />,         color: 'success' },
    { label: 'Open Rate',      value: fmtPct(overview.emailStats?.openRate),       icon: <Eye size={20} />,                 color: 'info' },
    { label: 'Click Rate',     value: fmtPct(overview.emailStats?.clickRate),      icon: <MousePointerClick size={20} />,   color: 'warning' },
  ] : [];

  const statusCards = overview ? [
    { label: 'Draft',     count: overview.campaigns?.draft ?? 0,     color: 'neutral' },
    { label: 'Scheduled', count: overview.campaigns?.scheduled ?? 0, color: 'info' },
    { label: 'Sending',   count: overview.campaigns?.sending ?? 0,   color: 'warning' },
    { label: 'Completed', count: overview.campaigns?.completed ?? 0, color: 'success' },
  ] : [];

  const getChartVal = (d, key) => parseInt(d[key] || 0, 10);
  const getChartSent = (d) => getChartVal(d, 'total') || getChartVal(d, 'sent');
  const getChartLabel = (d) => {
    if (d.period) return d.period;
    if (d.date) {
      const dt = new Date(d.date);
      return `${dt.getDate()}/${dt.getMonth() + 1}`;
    }
    return '';
  };
  const chartMax = chartData.length > 0 ? Math.max(...chartData.map(d => Math.max(getChartSent(d), getChartVal(d, 'delivered'), getChartVal(d, 'opened'))), 1) : 100;

  return (
    <div className="emd-page">
      {/* Header */}
      <div className="emd-header">
        <div>
          <h1 className="emd-title">Email Dashboard</h1>
          <p className="emd-subtitle">Monitor your email marketing performance</p>
        </div>
        <button className={`em-btn em-btn-secondary ${refreshing ? 'spinning' : ''}`} onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw size={16} className={refreshing ? 'spin-icon' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* ── Onboarding Setup Banner ── */}
      {showOnboarding && onboardingSteps && (
        <div className="emd-onboarding-card">
          <div className="emd-onboarding-header">
            <div>
              <h2 className="emd-onboarding-title">Complete Your Email Setup</h2>
              <p className="emd-onboarding-sub">
                {onboardingProgress.completed} of {onboardingProgress.total} steps done — finish setup to start sending emails
              </p>
            </div>
            <div className="emd-onboarding-progress">
              <div className="emd-onboarding-bar">
                <div className="emd-onboarding-fill" style={{ width: `${(onboardingProgress.completed / onboardingProgress.total) * 100}%` }} />
              </div>
              <span className="emd-onboarding-pct">{Math.round((onboardingProgress.completed / onboardingProgress.total) * 100)}%</span>
            </div>
          </div>
          <div className="emd-onboarding-steps">
            {onboardingSteps.map((s) => {
              const cfg = STEP_CONFIG[s.step_name] || { icon: <CheckCircle size={16} />, label: s.step_name, desc: '', path: '' };
              return (
                <div
                  key={s.step_name}
                  className={`emd-onboarding-step ${s.is_completed ? 'done' : ''}`}
                  onClick={() => !s.is_completed && cfg.path && navigate(cfg.path)}
                  style={{ cursor: s.is_completed ? 'default' : 'pointer' }}
                >
                  <div className={`emd-onboarding-step-icon ${s.is_completed ? 'done' : ''}`}>
                    {s.is_completed ? <CheckCircle size={16} /> : cfg.icon}
                  </div>
                  <div className="emd-onboarding-step-info">
                    <span className="emd-onboarding-step-label">{cfg.label}</span>
                    <span className="emd-onboarding-step-desc">{cfg.desc}</span>
                  </div>
                  {!s.is_completed && cfg.path && <ArrowRight size={14} className="emd-onboarding-step-arrow" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Sender From-Address Banner ── */}
      {fromPreview && (
        <div className="emd-from-banner">
          <div className="emd-from-icon">
            <Mail size={16} />
          </div>
          <div className="emd-from-info">
            <span className="emd-from-label">Sending as:</span>
            <span className="emd-from-address">{fromPreview.fromAddress}</span>
            {fromPreview.customDomainRegistered && !fromPreview.customDomainVerified && (
              <span className="emd-from-pending">Custom domain pending verification</span>
            )}
          </div>
          <div className="emd-from-actions">
            <button className="em-btn em-btn-ghost em-btn-sm" onClick={() => navigate('/dashboard/email/settings/sender-profile')}>
              <Settings size={13} />
              Profile
            </button>
            <button className="em-btn em-btn-ghost em-btn-sm" onClick={() => navigate('/dashboard/email/settings/custom-domain')}>
              <Globe size={13} />
              Domain
            </button>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="emd-kpi-row">
        {loading ? (
          [0,1,2,3].map(i => (
            <div key={i} className="emd-kpi-card">
              <div className="em-skl" style={{ width: 44, height: 44, borderRadius: 12 }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div className="em-skl" style={{ width: '60%', height: 28 }} />
                <div className="em-skl" style={{ width: '40%', height: 14 }} />
              </div>
            </div>
          ))
        ) : kpiCards.map((k, i) => (
          <div key={i} className={`emd-kpi-card emd-kpi-${k.color}`}>
            <div className={`emd-kpi-icon emd-kpi-icon-${k.color}`}>{k.icon}</div>
            <div>
              <h3 className="emd-kpi-value">{k.value}</h3>
              <p className="emd-kpi-label">{k.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Status Row */}
      {!loading && (
        <div className="emd-status-row">
          {statusCards.map((s, i) => (
            <div key={i} className="emd-status-card">
              <span className={`emd-status-dot emd-dot-${s.color}`}></span>
              <span className="emd-status-count">{s.count}</span>
              <span className="emd-status-label">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Main Grid */}
      <div className="emd-grid">
        {/* Chart */}
        <div className="em-card emd-chart-card">
          <div className="em-card-header">
            <div>
              <h2 className="em-card-title">Sending Trend</h2>
              <p className="em-card-sub">Emails over time</p>
            </div>
            <div className="emd-period-tabs">
              {['24h','7d','30d','90d'].map(p => (
                <button key={p} className={`emd-period-btn ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>{p}</button>
              ))}
            </div>
          </div>

          <div className="emd-chart-area">
            {loadingChart ? (
              <div className="emd-chart-skeleton">
                {[0,1,2,3,4,5,6].map(i => (
                  <div key={i} className="em-skl" style={{ height: `${30 + Math.random() * 50}%`, animationDelay: `${i * 80}ms` }} />
                ))}
              </div>
            ) : chartData.length === 0 ? (
              <div className="em-empty" style={{ padding: '2rem' }}>
                <div className="em-empty-icon"><BarChart3 size={24} /></div>
                <h3>No data yet</h3>
                <p>Send your first email campaign to see analytics</p>
              </div>
            ) : (
              <div className="emd-bar-chart">
                <div className="emd-chart-bars">
                  {chartData.map((d, i) => (
                    <div key={i} className="emd-bar-group" title={`Sent: ${getChartSent(d)} | Delivered: ${getChartVal(d, 'delivered')} | Opened: ${getChartVal(d, 'opened')}`}>
                      <div className="emd-bar emd-bar-sent" style={{ height: `${(getChartSent(d) / chartMax) * 100}%` }} />
                      <div className="emd-bar emd-bar-delivered" style={{ height: `${(getChartVal(d, 'delivered') / chartMax) * 100}%` }} />
                      <div className="emd-bar emd-bar-opened" style={{ height: `${(getChartVal(d, 'opened') / chartMax) * 100}%` }} />
                      <span className="emd-bar-label">{getChartLabel(d)}</span>
                    </div>
                  ))}
                </div>
                <div className="emd-chart-legend">
                  <span className="emd-legend-item"><span className="emd-legend-dot" style={{ background: 'var(--em-primary)' }}></span>Sent</span>
                  <span className="emd-legend-item"><span className="emd-legend-dot" style={{ background: 'var(--em-success)' }}></span>Delivered</span>
                  <span className="emd-legend-item"><span className="emd-legend-dot" style={{ background: 'var(--em-info)' }}></span>Opened</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recent Campaigns */}
        <div className="em-card emd-recent-card">
          <div className="em-card-header">
            <div>
              <h2 className="em-card-title">Recent Campaigns</h2>
              <p className="em-card-sub">Latest activity</p>
            </div>
          </div>

          <div className="emd-recent-list">
            {loading ? (
              [0,1,2,3].map(i => (
                <div key={i} className="emd-recent-item">
                  <div className="em-skl" style={{ width: '65%', height: 16 }} />
                  <div className="em-skl" style={{ width: '30%', height: 12, marginTop: 6 }} />
                </div>
              ))
            ) : !overview?.recentCampaigns?.length ? (
              <div className="em-empty" style={{ padding: '1.5rem' }}>
                <div className="em-empty-icon"><FileText size={24} /></div>
                <h3>No campaigns yet</h3>
                <p>Create your first email campaign</p>
              </div>
            ) : overview.recentCampaigns.map((c, i) => (
              <div key={c.id || i} className="emd-recent-item">
                <div className="emd-recent-info">
                  <p className="emd-recent-name">{c.name}</p>
                  <p className="emd-recent-meta">
                    {c.total_recipients || 0} recipients • {c.status}
                  </p>
                </div>
                <span className={`em-badge em-badge-${getStatusColor(c.status)}`}>
                  {c.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Unsubscribe Stats Widget */}
        <div className="em-card emd-unsub-card">
          <div className="em-card-header">
            <div>
              <h2 className="em-card-title">Unsubscribes</h2>
              <p className="em-card-sub">By category</p>
            </div>
            <UserX size={18} style={{ color: 'var(--em-text-3)' }} />
          </div>

          {unsubLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[0,1,2,3].map(i => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div className="em-skl" style={{ width: '55%', height: 14 }} />
                  <div className="em-skl" style={{ width: '20%', height: 14 }} />
                </div>
              ))}
            </div>
          ) : unsubStats.length === 0 ? (
            <div className="em-empty" style={{ padding: '1.5rem' }}>
              <div className="em-empty-icon"><UserX size={24} /></div>
              <h3>No unsubscribes</h3>
              <p>All recipients are subscribed</p>
            </div>
          ) : (
            <div className="emd-unsub-list">
              {unsubStats.map((s, i) => {
                const count = parseInt(s.count || 0, 10);
                const maxCount = Math.max(...unsubStats.map(x => parseInt(x.count || 0, 10)), 1);
                const pct = (count / maxCount) * 100;
                return (
                  <div key={s.category || i} className="emd-unsub-item">
                    <div className="emd-unsub-info">
                      <span className="emd-unsub-cat">
                        {(s.category || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                      <span className="emd-unsub-count">{count}</span>
                    </div>
                    <div className="emd-unsub-bar-bg">
                      <div className="emd-unsub-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function getStatusColor(status) {
  const map = {
    draft: 'neutral', scheduled: 'info', pending: 'warning',
    sending: 'warning', paused: 'danger', sent: 'success',
    completed: 'success', failed: 'danger',
  };
  return map[status] || 'neutral';
}

export default EmailDashboard;
